import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  MessageSquare,
  Users,
  Zap,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  User,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import {
  getAdminAIStats,
  getAdminAIToolStats,
  getAdminAIRecentConversations,
  getAdminAIUsageChart,
} from '../../services/adminService';
import type {
  AdminAIStats,
  AdminAIToolStats,
  AdminAIRecentConversation,
  AdminAIUsageChartPoint,
} from '../../types/admin';
import { logger } from '../../lib/logger';

// Tool action type labels
const TOOL_LABELS: Record<string, string> = {
  NAVIGATE: 'Navigation',
  UI_TOAST: 'Toast',
  LEAD_CREATE: 'Lead erstellen',
  LEAD_UPDATE: 'Lead aktualisieren',
  LEAD_GET: 'Lead abrufen',
  LEAD_LIST: 'Leads auflisten',
  TODO_CREATE: 'Todo erstellen',
  TODO_UPDATE: 'Todo aktualisieren',
  TODO_DELETE: 'Todo löschen',
  TODO_LIST: 'Todos auflisten',
  CAL_EVENT_CREATE: 'Termin erstellen',
  CAL_EVENT_UPDATE: 'Termin aktualisieren',
  CAL_EVENT_DELETE: 'Termin löschen',
  CAL_EVENT_LIST: 'Termine auflisten',
  PROFILE_GET: 'Profil abrufen',
  PROFILE_UPDATE: 'Profil aktualisieren',
};

// Simple area chart component
function SimpleAreaChart({
  data,
  dataKey,
  color,
  height = 120,
}: {
  data: AdminAIUsageChartPoint[];
  dataKey: 'conversations' | 'messages' | 'tokens';
  color: string;
  height?: number;
}) {
  if (!data || data.length === 0) return null;

  const values = data.map((d) => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min;

  const points = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * 100;
      const y = 100 - ((value - min) / range) * 100;
      return `${x},${y}`;
    })
    .join(' ');

  const areaPath = `M0,100 L${points
    .split(' ')
    .map((p, i) => {
      const [x, y] = p.split(',');
      return i === 0 ? `${x},${y}` : `L${x},${y}`;
    })
    .join(' ')} L100,100 Z`;

  return (
    <div style={{ height }} className="w-full">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id={`ai-gradient-${color}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#ai-gradient-${color})`} />
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2"
          points={points}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
}

// Format numbers for display
function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

// Format relative time
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Min.`;
  if (diffHours < 24) return `vor ${diffHours} Std.`;
  if (diffDays < 7) return `vor ${diffDays} Tagen`;
  return date.toLocaleDateString('de-DE');
}

export function AdminAI() {
  const [stats, setStats] = useState<AdminAIStats | null>(null);
  const [toolStats, setToolStats] = useState<AdminAIToolStats[]>([]);
  const [recentConversations, setRecentConversations] = useState<AdminAIRecentConversation[]>([]);
  const [usageChart, setUsageChart] = useState<AdminAIUsageChartPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    try {
      const [statsData, toolData, conversationsData, chartData] = await Promise.all([
        getAdminAIStats(),
        getAdminAIToolStats(),
        getAdminAIRecentConversations(15),
        getAdminAIUsageChart(30),
      ]);
      setStats(statsData);
      setToolStats(toolData);
      setRecentConversations(conversationsData);
      setUsageChart(chartData);
    } catch (error) {
      logger.error('Error loading AI admin data:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const successRate = stats?.tool_calls_total
    ? ((stats.successful_tool_calls / stats.tool_calls_total) * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
            <Bot className="w-8 h-8 text-violet-400" />
            AI Agent
          </h1>
          <p className="text-slate-400 mt-1">Statistiken und Übersicht des KI-Assistenten</p>
        </div>
        <button
          onClick={() => loadData(true)}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-violet-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{formatNumber(stats?.total_conversations || 0)}</p>
          <p className="text-sm text-slate-400 mt-1">Konversationen gesamt</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-xs font-medium text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full">
              7 Tage
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.conversations_7d || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Neue Konversationen</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.active_ai_users || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Nutzer mit AI</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-cyan-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.avg_messages_per_conversation || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Ø Nachrichten / Chat</p>
        </div>
      </div>

      {/* KPI Cards - Row 2 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{formatNumber(stats?.total_messages || 0)}</p>
          <p className="text-sm text-slate-400 mt-1">Nachrichten gesamt</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-orange-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{formatNumber(stats?.tool_calls_total || 0)}</p>
          <p className="text-sm text-slate-400 mt-1">Tool Calls</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{successRate}%</p>
          <p className="text-sm text-slate-400 mt-1">Erfolgsrate Tools</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Bot className="w-5 h-5 text-purple-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{formatNumber(stats?.total_tokens || 0)}</p>
          <p className="text-sm text-slate-400 mt-1">Tokens verwendet</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Usage Chart */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-white">AI Nutzung</h2>
              <p className="text-sm text-slate-400">Letzte 30 Tage</p>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-violet-500" />
                Konversationen
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                Nachrichten
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <SimpleAreaChart data={usageChart} dataKey="conversations" color="#8b5cf6" height={70} />
            <SimpleAreaChart data={usageChart} dataKey="messages" color="#22c55e" height={70} />
          </div>
          <div className="flex justify-between mt-4 text-xs text-slate-500">
            <span>Vor 30 Tagen</span>
            <span>Heute</span>
          </div>
        </div>

        {/* Tool Stats */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-white">Tool Aufrufe nach Typ</h2>
            <p className="text-sm text-slate-400">Alle Zeit</p>
          </div>
          <div className="space-y-3 max-h-[280px] overflow-y-auto">
            {toolStats.length === 0 ? (
              <p className="text-slate-500 text-center py-4">Keine Tool Calls vorhanden</p>
            ) : (
              toolStats.map((tool) => (
                <div key={tool.action_type} className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-300">
                        {TOOL_LABELS[tool.action_type] || tool.action_type}
                      </span>
                      <span className="text-sm font-medium text-white">{tool.total_calls}</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${tool.success_rate || 0}%`,
                          background:
                            tool.success_rate >= 90
                              ? 'linear-gradient(90deg, #22c55e, #4ade80)'
                              : tool.success_rate >= 70
                              ? 'linear-gradient(90deg, #f97316, #fb923c)'
                              : 'linear-gradient(90deg, #ef4444, #f87171)',
                        }}
                      />
                    </div>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      tool.success_rate >= 90
                        ? 'bg-green-500/20 text-green-400'
                        : tool.success_rate >= 70
                        ? 'bg-orange-500/20 text-orange-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}
                  >
                    {tool.success_rate}%
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Conversations */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-white">Letzte Konversationen</h2>
            <p className="text-sm text-slate-400">Die neuesten AI Chat-Verläufe</p>
          </div>
        </div>

        {recentConversations.length === 0 ? (
          <div className="text-center py-12">
            <Bot className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Noch keine Konversationen vorhanden</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentConversations.map((conv) => (
              <div
                key={conv.conversation_id}
                className="flex items-start gap-4 p-4 bg-slate-800/50 rounded-xl hover:bg-slate-800 transition-colors"
              >
                <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">
                        {conv.user_name || conv.user_email}
                      </p>
                      <p className="text-sm text-slate-400 truncate">
                        {conv.first_message || conv.title || 'Neue Konversation'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-xs text-slate-500">
                        {formatRelativeTime(conv.last_message_at || conv.created_at)}
                      </span>
                      <span className="text-xs bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full">
                        {conv.message_count} Nachr.
                      </span>
                    </div>
                  </div>
                </div>
                <Link
                  to={`/admin/users/${conv.user_id}`}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
                  title="Nutzer anzeigen"
                >
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Additional Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <User className="w-4 h-4 text-blue-400" />
            </div>
            <span className="text-slate-400 text-sm">User-Nachrichten</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatNumber(stats?.user_messages_total || 0)}</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-violet-500/20 rounded-lg flex items-center justify-center">
              <Bot className="w-4 h-4 text-violet-400" />
            </div>
            <span className="text-slate-400 text-sm">AI-Antworten</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatNumber(stats?.assistant_messages_total || 0)}</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-400" />
            </div>
            <span className="text-slate-400 text-sm">Erfolgreiche Tools</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatNumber(stats?.successful_tool_calls || 0)}</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-400" />
            </div>
            <span className="text-slate-400 text-sm">Fehlgeschlagene Tools</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.failed_tool_calls || 0}</p>
        </div>
      </div>
    </div>
  );
}
